import React, { lazy, useMemo } from 'react'
import { Navigate, Outlet, useLocation, useRoutes } from 'react-router-dom'

import { useFeatureIsOn } from '@growthbook/growthbook-react'
import { getDynamicPageId, getPathFromPageId } from 'helpers'
import { usePageInfo } from 'providers/PageInfoProvider'

import {
  selectCurrentVariantCohort,
  selectLanguage,
} from 'root-redux/selects/common'
import { selectUUID } from 'root-redux/selects/user'

import { useBulkSelector } from 'hooks/common/useBulkSelector'
import { useUpsellStatus } from 'hooks/user/useUpsellStatus'
import { useUserStatus } from 'hooks/user/useUserStatus'

import { IStep } from 'models/variant.model'

import { AllSetVariant1 } from 'pages/all-set'
import { DownloadApp } from 'pages/download-app'

import { PaymentProvider } from 'components/PaymentProvider'
import { ProtectedRoute } from 'components/ProtectedRoute'
import { SubscribeFlow } from 'components/SubscribeFlow'

import {
  DYNAMIC_PAGE_ID_PREFIX,
  GROWTH_BOOK_FEATURE_KEYS,
  PAGE_TYPE,
  PUBLIC_PAGES,
} from 'root-constants/common'
import { ID_TO_PAGE_MAP, PageId } from 'root-constants/pages'

const Account = lazy(() => import('modules/account/Account'))

const useSelectors = () =>
  useBulkSelector({
    cohort: selectCurrentVariantCohort,
    uuid: selectUUID,
    language: selectLanguage,
  })

export const RouteList: React.FC = () => {
  const { search, hash } = useLocation()
  const { cohort, uuid, language } = useSelectors()

  const availableRoute = useUserStatus()
  const isUpsellAvailable = useUpsellStatus()
  const isAllSetAvailable = useFeatureIsOn(
    GROWTH_BOOK_FEATURE_KEYS.car_2255_afterpay_celebration,
  )

  const {
    steps,
    currentSubscriptionPageId,
    currentUpsellPageId,
    currentPresalePageId,
  } = usePageInfo()

  const firstPagePath = useMemo(
    () =>
      getPathFromPageId({
        pageId: steps[0]?.id,
        cohort,
        uuid,
        language,
        currentSearch: search,
        hash,
      }),
    [cohort, language, search, steps, uuid, hash],
  )

  const { onboardingPages, subscriptionPages, upsellPages, presalePages } =
    useMemo(() => {
      return steps.reduce<{
        onboardingPages: IStep[]
        subscriptionPages: IStep[]
        presalePages: IStep[]
        upsellPages: IStep[]
      }>(
        (
          accum,
          { isSubscriptions, isPayment, isUpsell, pageType },
          ind,
          arr,
        ) => {
          if (pageType === PAGE_TYPE.PRESALE) {
            accum.presalePages.push(arr[ind])
            return accum
          }
          if (isSubscriptions || isPayment) {
            accum.subscriptionPages.push(arr[ind])
            return accum
          }

          if (isUpsell) {
            accum.upsellPages.push(arr[ind])
            return accum
          }

          accum.onboardingPages.push(arr[ind])
          return accum
        },
        {
          presalePages: [],
          onboardingPages: [],
          subscriptionPages: [],
          upsellPages: [],
        },
      )
    }, [steps])

  return useRoutes([
    { index: true, element: <Navigate to={firstPagePath} /> },
    {
      element: <Outlet />,
      children: [
        {
          element: <SubscribeFlow />,
          children: onboardingPages.map(({ id: pageId }, index, arr) => {
            const currentPageId = pageId.includes(DYNAMIC_PAGE_ID_PREFIX)
              ? getDynamicPageId(pageId)
              : pageId
            const CurrentPage = ID_TO_PAGE_MAP[currentPageId]

            const nextPagePath = arr[index + 1]
              ? getPathFromPageId({
                  pageId: arr[index + 1].id,
                  cohort,
                  uuid,
                  currentSearch: search,
                  language,
                  hash,
                })
              : ''

            const alternativePageId = arr.find(
              (dynamicPage, dynamicPageIndex) =>
                !dynamicPage.isSkippable && dynamicPageIndex > index,
            )?.id

            const alternativePagePath = getPathFromPageId({
              pageId: alternativePageId || arr[index + 1]?.id,
              cohort,
              uuid,
              language,
              currentSearch: search,
              hash,
            })

            if (PUBLIC_PAGES.includes(pageId)) {
              return {
                path: pageId,
                element: (
                  <CurrentPage
                    pageId={pageId}
                    nextPagePath={nextPagePath}
                    alternativePagePath="/"
                  />
                ),
              }
            }

            return {
              path: pageId,
              element: (
                <ProtectedRoute
                  isAvailable={!availableRoute}
                  availableRoute={availableRoute}
                >
                  <CurrentPage
                    pageId={pageId}
                    nextPagePath={nextPagePath}
                    alternativePagePath={alternativePagePath}
                  />
                </ProtectedRoute>
              ),
            }
          }),
        },
        {
          element: <SubscribeFlow />,
          children: presalePages.map(({ id: pageId }, index, arr) => {
            const PresalePage = ID_TO_PAGE_MAP[pageId]
            const nextPagePath = getPathFromPageId({
              pageId: arr[index + 1]?.id,
              cohort,
              uuid,
              language,
              currentSearch: search,
              hash,
            })

            return {
              path: pageId,
              element: (
                <ProtectedRoute
                  isAvailable={availableRoute === currentPresalePageId}
                  availableRoute={availableRoute}
                >
                  <PresalePage
                    pageId={pageId}
                    nextPagePath={nextPagePath}
                    alternativePagePath="/"
                  />
                </ProtectedRoute>
              ),
            }
          }),
        },
      ],
    },
    {
      element: (
        <PaymentProvider>
          <Outlet />
        </PaymentProvider>
      ),
      children: [
        {
          element: <SubscribeFlow />,
          children: subscriptionPages.map(({ id: pageId }, index, arr) => {
            const PurchasePage = ID_TO_PAGE_MAP[pageId]
            const nextPagePath = arr[index + 1]
              ? getPathFromPageId({
                  pageId: arr[index + 1].id,
                  cohort,
                  uuid,
                  currentSearch: search,
                  language,
                  hash,
                })
              : ''

            if (PUBLIC_PAGES.includes(pageId)) {
              return {
                path: pageId,
                element: (
                  <PurchasePage
                    pageId={pageId}
                    nextPagePath={nextPagePath}
                    alternativePagePath="/"
                  />
                ),
              }
            }

            return {
              path: pageId,
              element: (
                <ProtectedRoute
                  isAvailable={availableRoute === currentSubscriptionPageId}
                  availableRoute={availableRoute}
                >
                  <PurchasePage
                    pageId={pageId}
                    nextPagePath={nextPagePath}
                    alternativePagePath="/"
                  />
                </ProtectedRoute>
              ),
            }
          }),
        },
        {
          element: <SubscribeFlow />,
          children: upsellPages.map(({ id: pageId }, index, arr) => {
            const UpsellPage = ID_TO_PAGE_MAP[pageId]
            const nextPagePath = getPathFromPageId({
              pageId: arr[index + 1]?.id,
              cohort,
              uuid,
              language,
              currentSearch: search,
              hash,
            })

            return {
              path: pageId,
              element: (
                <ProtectedRoute
                  isAvailable={availableRoute === currentUpsellPageId}
                  availableRoute={availableRoute}
                >
                  <UpsellPage
                    pageId={pageId}
                    nextPagePath={nextPagePath}
                    alternativePagePath="/"
                  />
                </ProtectedRoute>
              ),
            }
          }),
        },
      ],
    },
    {
      path: PageId.ALL_SET_1,
      element: (
        <ProtectedRoute
          isAvailable={
            (availableRoute === currentUpsellPageId &&
              isUpsellAvailable &&
              isAllSetAvailable) ||
            availableRoute === PageId.ALL_SET_1
          }
          availableRoute={availableRoute}
        >
          <AllSetVariant1 />
        </ProtectedRoute>
      ),
    },
    {
      path: PageId.ACCOUNT,
      element: (
        <ProtectedRoute
          isAvailable={
            (availableRoute === currentUpsellPageId && isUpsellAvailable) ||
            availableRoute === PageId.ALL_SET_1 ||
            availableRoute === PageId.ACCOUNT
          }
          availableRoute={availableRoute}
        >
          <Account />
        </ProtectedRoute>
      ),
    },
    {
      path: PageId.DOWNLOAD,
      element: (
        <ProtectedRoute
          isAvailable={availableRoute === PageId.DOWNLOAD}
          availableRoute={availableRoute}
        >
          <DownloadApp />
        </ProtectedRoute>
      ),
    },
    { path: '*', element: <Navigate to={firstPagePath} /> },
  ])
}
